import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';
import { Composer } from '../components/Composer';
import { DebugPanel } from '../components/DebugPanel';
import { MessageList } from '../components/MessageList';
import { ArrowForward, Close } from '../components/ui/icons';
import { useAgent } from '../lib/agent';
import { api, DEBUG, readLastSeen, writeLastSeen, type HomeSummary } from '../lib/api';
import { timeFormatter } from '../lib/format';
import { displayFirstName } from '../lib/names';
import { useSession } from '../lib/session';
import { useDelayedFlag } from '../lib/viewport';

/** The connection banner waits this long before admitting the socket is not open. */
const CONNECTING_GRACE_MS = 1500;

/** The TWO destination: the conversation with your second self. */
export function TwoPage() {
  const { t, i18n } = useTranslation();
  const { me } = useSession();
  const agent = useAgent();
  const [summary, setSummary] = useState<HomeSummary | null>(null);
  const { hash } = useLocation();
  const focusEventId = hash.startsWith('#evt_') ? hash.slice(1) : null;
  const [focusDone, setFocusDone] = useState(false);
  const pagesLoaded = useRef(0);
  const { messages, loadingHistory, hasMore, loadOlder, clearUnseen } = agent;
  // The conversation is open: whatever TWO said while the person was away is now seen.
  useEffect(() => {
    clearUnseen();
  }, [clearUnseen, messages.length]);

  // Load enough history to include the linked message (at most ten older pages).
  useEffect(() => {
    if (!focusEventId || focusDone || loadingHistory) return;
    if (messages.some((m) => m.id === focusEventId)) return;
    if (!hasMore || pagesLoaded.current >= 10) return;
    pagesLoaded.current += 1;
    void loadOlder();
  }, [focusEventId, focusDone, loadingHistory, messages, hasMore, loadOlder]);
  const showConnection = useDelayedFlag(agent.status !== 'open', CONNECTING_GRACE_MS);

  // "While you were away" (M4.6): what happened since the last visit, shown once. Reading
  // the conversation (leaving this screen) or closing the card records last_seen, so the same
  // items never come back; only something newer brings the card again.
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    api
      .homeSummary(readLastSeen())
      .then((s) => {
        if (!cancelled) setSummary(s);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
      writeLastSeen();
    };
  }, []);
  const closeAway = () => {
    writeLastSeen();
    setDismissed(true);
  };

  const firstName = displayFirstName(me?.first_name) || me?.display_name || '';
  const away =
    summary &&
    !dismissed &&
    (summary.handled.count > 0 || summary.waiting.count > 0 || (summary.attention?.count ?? 0) > 0)
      ? summary
      : null;
  const timeFormat = timeFormatter(i18n.resolvedLanguage ?? 'en', {
    timeZone: me?.timezone ?? 'Asia/Qatar',
  });

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {showConnection ? (
        <div
          role="status"
          className="mx-4 mt-2 flex shrink-0 items-center justify-center gap-2 rounded-md bg-attention-soft px-3 py-1.5 text-xs text-attention-text"
        >
          <span aria-hidden className="size-1.5 animate-pulse rounded-full bg-attention" />
          {t(`two.status.${agent.status}`)}
        </div>
      ) : null}
      {me?.admission === 'waitlisted' ? (
        <div className="mx-4 mt-2 shrink-0 rounded-md bg-attention-soft px-3 py-2 text-center text-sm text-attention-text">
          {t('two.waitlisted')}
        </div>
      ) : null}

      {away ? (
        <section
          aria-label={t('two.away.title')}
          className="mx-4 mt-3 shrink-0 rounded-lg border border-line bg-surface p-4 shadow-sm dark:bg-elevated"
        >
          <div className="flex items-start justify-between gap-3">
            <h2 className="text-sm font-semibold text-ink">{t('two.away.title')}</h2>
            <button
              type="button"
              onClick={closeAway}
              aria-label={t('common.close')}
              className="-me-2 -mt-2 flex size-10 shrink-0 items-center justify-center rounded-md text-ink-2 hover:text-ink"
            >
              <Close size={18} />
            </button>
          </div>
          <div className="mt-2 grid grid-cols-3 gap-3">
            <div>
              <p className="tabular text-2xl font-semibold text-ink">{away.handled.count}</p>
              <p className="text-xs text-ink-2">{t('two.away.handled')}</p>
            </div>
            <div>
              <p className="tabular text-2xl font-semibold text-ink">{away.waiting.count}</p>
              <p className="text-xs text-ink-2">{t('two.away.waiting')}</p>
            </div>
            <div>
              <p
                className={[
                  'tabular text-2xl font-semibold',
                  (away.attention?.count ?? 0) > 0 ? 'text-critical-text' : 'text-ink',
                ].join(' ')}
              >
                {away.attention?.count ?? 0}
              </p>
              <p className="text-xs text-ink-2">{t('two.away.attention')}</p>
            </div>
          </div>
          {[...(away.attention?.items ?? []), ...away.waiting.items, ...away.handled.items].slice(
            0,
            4,
          ).length ? (
            <ul className="mt-3 flex flex-col divide-y divide-line">
              {[...(away.attention?.items ?? []), ...away.waiting.items, ...away.handled.items]
                .slice(0, 4)
                .map((item) => (
                  <li key={item.id} className="flex items-center gap-3 py-2 text-sm">
                    <span className="flex-1 truncate text-ink" dir="auto">
                      {item.title}
                      {item.error ? (
                        <span className="text-critical-text"> · {item.error}</span>
                      ) : null}
                    </span>
                    <time dateTime={item.at} className="tabular text-xs text-ink-3">
                      {timeFormat.format(new Date(item.at))}
                    </time>
                  </li>
                ))}
            </ul>
          ) : null}
          <Link
            to="/tasks"
            className="mt-2 inline-flex min-h-10 items-center gap-1 text-sm font-medium text-brand"
          >
            {t('two.away.open')}
            <ArrowForward size={16} />
          </Link>
        </section>
      ) : null}

      <MessageList
        messages={agent.messages}
        draft={agent.draft}
        turnState={agent.turnState}
        turnLabel={agent.turnLabel}
        steps={agent.steps}
        loading={agent.loadingHistory}
        hasMore={agent.hasMore}
        onLoadMore={agent.loadOlder}
        firstName={firstName}
        onSuggest={agent.send}
        focusEventId={focusDone ? null : focusEventId}
        onFocused={() => setFocusDone(true)}
        approvals={agent.approvals}
        schedules={agent.schedules}
        connectionActions={agent.connectionActions}
        approvalResults={agent.approvalResults}
        onApprove={async (a) => {
          await api.approveTask(a.task_id, { approval_id: a.approval_id, signature: a.signature });
        }}
        onCancel={async (a) => {
          await api.cancelTask(a.task_id, { approval_id: a.approval_id });
        }}
        onRetry={agent.retry}
      />
      {DEBUG ? (
        <DebugPanel
          intents={agent.intents}
          status={agent.status}
          messages={agent.messages}
          timings={agent.lastTimings}
        />
      ) : null}
      <Composer disabled={agent.status === 'unauthenticated'} onSend={agent.send} />
    </div>
  );
}
